/* ==========================================================================
   CustomFieldsEdit (PORTAL-147, PORTAL-164)

   Renders a list of custom field definitions as react-hook-form inputs under
   `formPrefix`. Must be rendered inside a FormProvider. Used by the product
   subform (product custom fields, prefix "custom_field_values") and by the
   New Prescription page for the pharmacy's general custom fields (prefix
   "general_custom_field_values").
   ========================================================================== */

import { useFormContext } from 'react-hook-form'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { FormField } from '@/components/ui/FormField'
import type { CustomFieldDefinition } from '@/api/types'

/**
 * Editable custom field inputs. Rendered in BOTH the summary and edit views
 * (PORTAL-147) — see the call sites for why there is no read-only counterpart.
 */
export interface CustomFieldsEditProps {
  fields: CustomFieldDefinition[]
  disabled: boolean
  /** react-hook-form path prefix, e.g. "custom_field_values". */
  formPrefix: string
  /** Section heading; defaults to "Additional Information". */
  title?: string
  /** Drop the top border/heading wrapper (when the host renders its own Card). */
  bare?: boolean
}

export function CustomFieldsEdit({
  fields,
  disabled,
  formPrefix,
  title = 'Additional Information',
  bare = false,
}: CustomFieldsEditProps) {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext()

  return (
    <div className={bare ? '' : 'border-t border-[var(--color-gray-200)] pt-4 mt-4'}>
      {!bare && (
        <h4 className="text-sm font-semibold text-[var(--color-gray-500)] mb-3">
          {title}
        </h4>
      )}
      <div className="space-y-4">
        {fields.map((field) => {
          const fieldName = `${formPrefix}.${field.key}`
          const fieldError = (errors as Record<string, any>)?.[formPrefix]?.[field.key]

          if (field.type === 'text') {
            return (
              <FormField
                key={field.key}
                label={field.label}
                required={field.required}
                htmlFor={fieldName}
                error={fieldError?.message}
                helperText={field.helper_text || undefined}
              >
                <Input
                  id={fieldName}
                  type="text"
                  disabled={disabled}
                  placeholder={field.placeholder || undefined}
                  error={!!fieldError}
                  {...register(fieldName, {
                    required: field.required ? `${field.label} is required` : false,
                    maxLength: { value: 2000, message: 'Max 2000 characters' },
                  })}
                />
              </FormField>
            )
          }

          if (field.type === 'number') {
            return (
              <FormField
                key={field.key}
                label={field.label}
                required={field.required}
                htmlFor={fieldName}
                error={fieldError?.message}
                helperText={field.helper_text || undefined}
              >
                <Input
                  id={fieldName}
                  type="number"
                  disabled={disabled}
                  placeholder={field.placeholder || undefined}
                  error={!!fieldError}
                  {...register(fieldName, {
                    required: field.required ? `${field.label} is required` : false,
                    valueAsNumber: true,
                  })}
                />
              </FormField>
            )
          }

          if (field.type === 'date') {
            return (
              <FormField
                key={field.key}
                label={field.label}
                required={field.required}
                htmlFor={fieldName}
                error={fieldError?.message}
                helperText={field.helper_text || undefined}
              >
                <Input
                  id={fieldName}
                  type="date"
                  disabled={disabled}
                  error={!!fieldError}
                  {...register(fieldName, {
                    required: field.required ? `${field.label} is required` : false,
                  })}
                />
              </FormField>
            )
          }

          if (field.type === 'dropdown' && field.options) {
            const currentValue = watch(fieldName)
            const options = field.options.map((o) => ({ value: o, label: o }))

            return (
              <FormField
                key={field.key}
                label={field.label}
                required={field.required}
                htmlFor={fieldName}
                error={fieldError?.message}
                helperText={field.helper_text || undefined}
              >
                <Select
                  options={options}
                  value={currentValue || null}
                  onChange={(v) => setValue(fieldName, v || '', { shouldValidate: true })}
                  placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`}
                  disabled={disabled}
                  error={!!fieldError}
                />
                {/* Hidden input for required validation */}
                <input
                  type="hidden"
                  {...register(fieldName, {
                    required: field.required ? `${field.label} is required` : false,
                  })}
                />
              </FormField>
            )
          }

          if (field.type === 'checkbox') {
            const currentValue = watch(fieldName)

            return (
              <div key={field.key}>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!currentValue}
                    onChange={(e) =>
                      setValue(fieldName, e.target.checked, { shouldValidate: true })
                    }
                    disabled={disabled}
                    className="rounded border-[var(--color-gray-300)] text-[var(--pharmacy-primary)] focus:ring-[var(--pharmacy-primary)]"
                  />
                  <span className="text-[var(--color-charcoal)]">{field.label}</span>
                  {field.required && (
                    <span className="text-[var(--color-error)]">*</span>
                  )}
                </label>
                {field.helper_text && (
                  <p className="ml-6 mt-1 text-xs text-[var(--color-gray-500)]">
                    {field.helper_text}
                  </p>
                )}
                {/* PORTAL-147: surface the error. Every other field type gets
                    one from FormField; without it a required-but-unchecked box
                    failed trigger() silently — submit simply did nothing, with
                    no marker anywhere on the form. */}
                {fieldError?.message && (
                  <p className="ml-6 mt-1 text-sm text-error" role="alert">
                    {String(fieldError.message)}
                  </p>
                )}
                {/* Hidden validation */}
                {field.required && (
                  <input
                    type="hidden"
                    {...register(fieldName, {
                      validate: (v: boolean) =>
                        field.required && !v ? `${field.label} is required` : true,
                    })}
                  />
                )}
              </div>
            )
          }

          return null
        })}
      </div>
    </div>
  )
}
